import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, Phone, AlertTriangle } from 'lucide-react';

const FALLBACK_SERVICES = [
  'Manicure (Classic)',
  'Manicure (Gel)',
  'Pedicure (Classic)',
  'Pedicure (Spa)',
  'Gel Nails (Full Set)',
  'Acrylic Nails (Full Set)',
  'Acrylic Fill-In',
  'Nail Art / 3D Design',
  'Spray Tan (Custom Blend)',
  'Spray Tan (Express)',
  'Signature Facial',
  'Hydrating Facial',
  'UV Blue Light Therapy',
  'Pro Makeup (Event)',
  'Pro Makeup (Bridal)',
  'Hair Styling (Blowout)',
  'Hair Styling (Event)',
];

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BookNow = () => {
  const [searchParams] = useSearchParams();
  const prefillService = searchParams.get('service') || '';
  const [services, setServices] = useState(FALLBACK_SERVICES);
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    service: prefillService || FALLBACK_SERVICES[0],
    date: '',
    time: '',
    notes: '',
    emergency: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  // Pull live services from the Service Menu so admin can manage them
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/services?active_only=true`);
        if (Array.isArray(r.data) && r.data.length > 0) {
          const names = r.data.map(s => `${s.name}${s.price ? ` — $${s.price}` : ''}`);
          setServices(names);

          // Smart matching: card name may be plural/branded ("Spray Tans"), backend may be
          // singular/specific ("Spray Tan (Custom)"). Strip trailing 's' + lowercase includes.
          const findMatch = (want) => {
            if (!want) return null;
            const stripped = want.toLowerCase().replace(/s\b/g, '');
            const keywordMap = {
              'luxury nail': 'gel nail',
              'nail': 'gel nail',
              'pro makeup': 'pro makeup',
              'makeup': 'pro makeup',
              'hair styling': 'hair styling',
              'hair': 'hair styling',
              'facial': 'signature facial',
              'ai 3d': 'ai 3d nail art',
            };
            const mapped = keywordMap[stripped.trim()] || stripped;
            // 1. Exact prefix on stripped want
            let hit = names.find(n => n.toLowerCase().startsWith(want.toLowerCase()));
            // 2. Includes on mapped keyword
            if (!hit) hit = names.find(n => n.toLowerCase().includes(mapped));
            // 3. Includes on any word of want
            if (!hit) {
              const words = stripped.split(/\s+/).filter(w => w.length > 2);
              hit = names.find(n => words.every(w => n.toLowerCase().includes(w)));
            }
            return hit || null;
          };

          setForm(f => {
            const match = findMatch(prefillService);
            return { ...f, service: match || names[0] };
          });
        }
      } catch {
        // fallback list already set
      }
    })();
    // eslint-disable-next-line
  }, []);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      // First create or fetch client by phone
      const clientPayload = {
        name: form.name,
        email: form.email || `${form.phone}@placeholder.com`,
        phone: form.phone,
        subscription_tier: 'None',
      };
      let clientId = null;
      try {
        const cr = await axios.post(`${API_URL}/api/clients`, clientPayload);
        clientId = cr.data?.id;
      } catch {
        // ignore — may already exist
      }

      const appointmentPayload = {
        client_id: clientId || 'guest',
        client_name: form.name,
        employee_id: 'unassigned',
        employee_name: 'To Be Assigned',
        service: `${form.service}${form.emergency ? ' (⚡ EMERGENCY)' : ''}${form.notes ? ' — ' + form.notes : ''}`,
        date: form.date && form.time ? `${form.date}T${form.time}:00` : new Date().toISOString(),
        status: 'pending',
      };
      await axios.post(`${API_URL}/api/appointments`, appointmentPayload);

      setSubmitted(true);
    } catch (err) {
      setError('Something went wrong. Please call us at 904.672.9966 to book directly.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="py-32 text-center px-6" data-testid="book-success">
        <Sparkles className="w-12 h-12 text-pink-300 mx-auto mb-6" />
        <h1 className="font-serif text-5xl text-amber-50">You're booked!</h1>
        <p className="mt-4 text-amber-100/70 max-w-md mx-auto">
          We received your request. Christina will text you at <span className="text-amber-200">{form.phone}</span> shortly to confirm.
        </p>
        <a href="tel:9046729966" className="inline-flex items-center gap-2 mt-10 px-8 py-4 rounded-full border border-amber-700/50 text-amber-100" data-testid="book-call">
          <Phone className="w-4 h-4" /> Need it sooner? Call 904.672.9966
        </a>
      </div>
    );
  }

  return (
    <div className="py-20" data-testid="book-page">
      <div className="max-w-3xl mx-auto px-6">
        <div className="text-center mb-12">
          <div className="text-[10px] tracking-[0.35em] uppercase text-amber-300/70 mb-4">✦ Book Your Glow ✦</div>
          <h1 className="font-serif text-5xl sm:text-6xl text-amber-50" data-testid="book-title">
            Reserve your <span className="italic text-pink-300">appointment</span>
          </h1>
          <p className="mt-4 text-amber-100/60">
            Fill out the form. Christina will text you to confirm. Same-day before 6PM available.
          </p>
        </div>

        <form onSubmit={onSubmit} className="rounded-2xl border border-amber-900/30 bg-[#0a0405] p-8 md:p-10 space-y-5" data-testid="book-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <label className="block">
              <span className="text-xs tracking-wider uppercase text-amber-300/70">Full Name</span>
              <input
                name="name"
                required
                value={form.name}
                onChange={onChange}
                className="mt-2 w-full bg-black/40 border border-amber-900/30 rounded-md px-4 py-3 text-amber-50 focus:outline-none focus:border-pink-400/30"
                placeholder="Jane Doe"
                data-testid="input-name"
              />
            </label>
            <label className="block">
              <span className="text-xs tracking-wider uppercase text-amber-300/70">Phone</span>
              <input
                name="phone"
                required
                type="tel"
                value={form.phone}
                onChange={onChange}
                className="mt-2 w-full bg-black/40 border border-amber-900/30 rounded-md px-4 py-3 text-amber-50 focus:outline-none focus:border-pink-400/30"
                placeholder="(904) 555-0123"
                data-testid="input-phone"
              />
            </label>
          </div>

          <label className="block">
            <span className="text-xs tracking-wider uppercase text-amber-300/70">Email (optional)</span>
            <input
              name="email"
              type="email"
              value={form.email}
              onChange={onChange}
              className="mt-2 w-full bg-black/40 border border-amber-900/30 rounded-md px-4 py-3 text-amber-50 focus:outline-none focus:border-pink-400/30"
              placeholder="you@email.com"
              data-testid="input-email"
            />
          </label>

          <label className="block">
            <span className="text-xs tracking-wider uppercase text-amber-300/70">Service</span>
            <select
              name="service"
              value={form.service}
              onChange={onChange}
              className="mt-2 w-full bg-black/40 border border-amber-900/30 rounded-md px-4 py-3 text-amber-50 focus:outline-none focus:border-pink-400/30"
              data-testid="input-service"
            >
              {services.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <label className="block">
              <span className="text-xs tracking-wider uppercase text-amber-300/70">Date</span>
              <input
                name="date"
                type="date"
                required
                value={form.date}
                onChange={onChange}
                className="mt-2 w-full bg-black/40 border border-amber-900/30 rounded-md px-4 py-3 text-amber-50 focus:outline-none focus:border-pink-400/30"
                data-testid="input-date"
              />
            </label>
            <label className="block">
              <span className="text-xs tracking-wider uppercase text-amber-300/70">Time</span>
              <input
                name="time"
                type="time"
                required
                value={form.time}
                onChange={onChange}
                className="mt-2 w-full bg-black/40 border border-amber-900/30 rounded-md px-4 py-3 text-amber-50 focus:outline-none focus:border-pink-400/30"
                data-testid="input-time"
              />
            </label>
          </div>

          <label className="block">
            <span className="text-xs tracking-wider uppercase text-amber-300/70">Notes / Inspiration</span>
            <textarea
              name="notes"
              rows={3}
              value={form.notes}
              onChange={onChange}
              className="mt-2 w-full bg-black/40 border border-amber-900/30 rounded-md px-4 py-3 text-amber-50 focus:outline-none focus:border-pink-400/30"
              placeholder="Any design ideas? Address? Allergies?"
              data-testid="input-notes"
            />
          </label>

          <label className="flex items-start gap-3 cursor-pointer rounded-lg border border-pink-400/30 bg-pink-950/20 p-4" data-testid="emergency-toggle">
            <input
              type="checkbox"
              name="emergency"
              checked={form.emergency}
              onChange={onChange}
              className="mt-1 w-4 h-4 accent-pink-400"
              data-testid="input-emergency"
            />
            <div>
              <div className="flex items-center gap-2 text-amber-100 font-medium">
                <AlertTriangle className="w-4 h-4 text-pink-300" />
                Emergency — within the hour
              </div>
              <div className="text-xs text-amber-100/60 mt-1">
                Checking this adds an emergency upcharge. We'll do our best to arrive within 60 minutes. Subject to availability.
              </div>
            </div>
          </label>

          {error && <div className="text-pink-300 text-sm" data-testid="book-error">{error}</div>}

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-4 rounded-full bg-pink-300 text-black font-medium hover:bg-pink-200 transition-all disabled:opacity-60"
            data-testid="book-submit"
          >
            {submitting ? 'Submitting...' : 'Request Appointment'}
          </button>

          <p className="text-center text-xs text-amber-100/50">
            Or call directly: <a href="tel:9046729966" className="text-amber-200">904.672.9966</a>
          </p>
        </form>
      </div>
    </div>
  );
};

export default BookNow;
